import { useEffect } from "react";

/**
 * useIframeHeight
 *
 * Mide la altura real del contenido de la app y la envia al portal
 * contenedor (Joomla / SP Page Builder) mediante postMessage, para que
 * el iframe ajuste su altura dinamicamente.
 *
 * Se recomienda usarlo una sola vez, a nivel de App, para que aplique a
 * todas las rutas.
 *
 * Formato del mensaje enviado:
 *   { type: "certificados:resize", height: <numero en px> }
 *
 * @param {Object}  [options]
 * @param {string}  [options.messageType]  Identificador del mensaje (default "certificados:resize").
 * @param {string}  [options.targetOrigin] Origen destino del postMessage. Por defecto "*".
 *                                          En produccion es recomendable fijar el dominio del portal.
 */
export const useIframeHeight = ({
  messageType = "certificados:resize",
  targetOrigin = "*",
} = {}) => {
  useEffect(() => {
    // Si no estamos dentro de un iframe, no hay nada que notificar.
    const isEmbedded = window.self !== window.top;

    let lastHeight = 0;

    const getHeight = () => {
      // Medimos el contenedor raiz de React (#root), NO el body ni el
      // documentElement. El body/html heredan el alto que el iframe les
      // presta, asi que nunca encogen aunque el contenido sea menor.
      // #root, en cambio, solo ocupa lo que su contenido necesita, por
      // lo que refleja el alto real tanto al crecer como al encoger.
      const root = document.getElementById("root");
      const el = root || document.body;
      return el ? Math.ceil(el.getBoundingClientRect().height) : 0;
    };

    const sendHeight = () => {
      const height = getHeight();
      if (height <= 0) return;
      // Evitamos enviar mensajes repetidos o variaciones minimas (1-2px)
      // que pueden retroalimentar un bucle de crecimiento.
      if (Math.abs(height - lastHeight) < 3) return;
      lastHeight = height;

      const message = { type: messageType, height };

      // Notificamos al contenedor. Si no estamos embebidos, igualmente
      // publicamos por si el entorno de pruebas lo necesita.
      const target = isEmbedded ? window.parent : window;
      target.postMessage(message, targetOrigin);
    };

    // 1) Envio inicial.
    sendHeight();

    // 2) Observamos cambios en el tamano del contenido (responsive,
    //    carga de datos, aparicion/ocultacion de elementos, etc.).
    const rootEl = document.getElementById("root") || document.body;

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && rootEl) {
      resizeObserver = new ResizeObserver(() => sendHeight());
      // Observamos el contenedor raiz (#root), que refleja el alto real
      // del contenido tanto cuando crece como cuando encoge.
      resizeObserver.observe(rootEl);
    }

    // 3) Eventos adicionales que pueden alterar la altura.
    window.addEventListener("load", sendHeight);
    window.addEventListener("resize", sendHeight);
    // Al volver con el boton "atras" (SPA), el contenido cambia sin
    // recargar; reenviamos para que el iframe se ajuste (crezca o encoja).
    window.addEventListener("popstate", sendHeight);
    window.addEventListener("pageshow", sendHeight);

    // 4) Observamos tambien cambios en el DOM (cambios de ruta que
    //    reemplazan el contenido) para reportar cuando la altura baja.
    let mutationObserver;
    if (typeof MutationObserver !== "undefined" && rootEl) {
      mutationObserver = new MutationObserver(() => sendHeight());
      mutationObserver.observe(rootEl, {
        childList: true,
        subtree: true,
      });
    }

    // 5) Fallback: algunos cambios (fuentes, imagenes, navegacion) no
    //    siempre disparan los observers. Reintentamos varias veces.
    const timers = [
      setTimeout(sendHeight, 100),
      setTimeout(sendHeight, 300),
      setTimeout(sendHeight, 1000),
    ];

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
      window.removeEventListener("load", sendHeight);
      window.removeEventListener("resize", sendHeight);
      window.removeEventListener("popstate", sendHeight);
      window.removeEventListener("pageshow", sendHeight);
      timers.forEach(clearTimeout);
    };
  }, [messageType, targetOrigin]);
};
